import React, { useEffect, useMemo, useState } from "react";
import {
  getMaterialLabel,
  MATERIAL_DATABASE,
  TOOL_COATINGS,
  TOOL_MATERIAL_OPTIONS,
} from "../../data/machiningMaterials";
import MaterialSelector from "../MaterialSelector";
import ToolCoatingSelector from "../ToolCoatingSelector";

const COOLANT_OPTIONS = {
  dry: {
    label: "Dry",
    multiplier: 1.12,
  },
  mist: {
    label: "Mist",
    multiplier: 1.0,
  },
  flood: {
    label: "Flood",
    multiplier: 0.88,
  },
};

const getWorkpieceMaterial = (materialKey) => {
  const material = MATERIAL_DATABASE[materialKey];
  if (!material) return null;

  const machinability = Number(material.machinability || 50);
  const hardnessFactor = material.hardnessHB ? Math.max(0.75, material.hardnessHB / 180) : 1;
  const machinabilityFactor = Math.max(0.6, Math.min(2.1, 70 / machinability));

  return {
    label: material.label,
    difficulty: roundTo((machinabilityFactor + hardnessFactor) / 2, 3),
  };
};

const createCutId = () =>
  `wear_cut_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const createCut = (id, radialDepth, axialDepth, cutLength, passCount) => ({
  id,
  radialDepth,
  axialDepth,
  cutLength,
  passCount,
});

const DEFAULT_PARAMS = {
  units: "inch",
  toolMaterial: "carbide",
  toolCoating: "uncoated",
  workpieceMaterial: "mild_steel",
  coolant: "mist",
  toolDiameter: 0.5,
  stickoutLength: 1.5,
  spindleRPM: 2500,
  feedRate: 12,
};

const DEFAULT_CUTS = [createCut("wear_cut_1", 0.05, 0.25, 1.0, 1)];
const SAVED_TOOLS_KEY = "manucalc_saved_tools_v1";

const loadSavedTools = () => {
  try {
    return JSON.parse(localStorage.getItem(SAVED_TOOLS_KEY) || "[]");
  } catch {
    return [];
  }
};

const writeSavedTools = (tools) => {
  localStorage.setItem(SAVED_TOOLS_KEY, JSON.stringify(tools));
};

const roundTo = (value, decimals = 4) => {
  if (!Number.isFinite(value)) return 0;
  return Number(value.toFixed(decimals));
};

const convertLengthToMillimeters = (value, units) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return units === "metric" ? numeric : numeric * 25.4;
};

const convertFeedToMMPerMin = (value, units) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return units === "metric" ? numeric : numeric * 25.4;
};

const getWearStatus = (percent) => {
  if (percent < 35) return "Low";
  if (percent < 65) return "Moderate";
  if (percent < 85) return "High";
  return "Critical";
};

const calculateToolWear = ({ params, cuts }) => {
  const errors = {};
  const warnings = [];

  const units = params.units || "inch";
  const toolMaterial = TOOL_MATERIAL_OPTIONS[params.toolMaterial];
  const toolCoating = TOOL_COATINGS[params.toolCoating] || TOOL_COATINGS.uncoated;
  const workpieceMaterial = getWorkpieceMaterial(params.workpieceMaterial);
  const coolant = COOLANT_OPTIONS[params.coolant];

  const toolDiameter = Number(params.toolDiameter);
  const stickoutLength = Number(params.stickoutLength);
  const spindleRPM = Number(params.spindleRPM);
  const feedRate = Number(params.feedRate);

  if (!toolMaterial) errors.toolMaterial = "Invalid tool material.";
  if (!toolCoating) errors.toolCoating = "Invalid tool coating.";
  if (!workpieceMaterial) errors.workpieceMaterial = "Invalid workpiece material.";
  if (!coolant) errors.coolant = "Invalid coolant selection.";
  if (params.toolDiameter === "" || params.toolDiameter === null || params.toolDiameter === undefined) errors.toolDiameter = "More data required";
  else if (!toolDiameter || toolDiameter <= 0) errors.toolDiameter = "Tool diameter must be greater than zero.";
  if (params.stickoutLength === "" || params.stickoutLength === null || params.stickoutLength === undefined) errors.stickoutLength = "More data required";
  else if (!stickoutLength || stickoutLength <= 0) errors.stickoutLength = "Tool stickout must be greater than zero.";
  if (params.spindleRPM === "" || params.spindleRPM === null || params.spindleRPM === undefined) errors.spindleRPM = "More data required";
  else if (!spindleRPM || spindleRPM <= 0) errors.spindleRPM = "Spindle RPM must be greater than zero.";
  if (params.feedRate === "" || params.feedRate === null || params.feedRate === undefined) errors.feedRate = "More data required";
  else if (!feedRate || feedRate <= 0) errors.feedRate = "Feed rate must be greater than zero.";
  if (!Array.isArray(cuts) || cuts.length === 0) errors.cuts = "At least one cut is required.";

  const normalizedCuts = (cuts || []).map((cut, index) => {
    const radialDepth = Number(cut.radialDepth);
    const axialDepth = Number(cut.axialDepth);
    const cutLength = Number(cut.cutLength);
    const passCount = Number(cut.passCount);

    if (cut.radialDepth === "" || cut.radialDepth === null || cut.radialDepth === undefined) {
      errors[`cut_${index}_radialDepth`] = "More data required";
    } else if (!radialDepth || radialDepth <= 0) {
      errors[`cut_${index}_radialDepth`] = `Cut ${index + 1} radial depth must be greater than zero.`;
    }

    if (cut.axialDepth === "" || cut.axialDepth === null || cut.axialDepth === undefined) {
      errors[`cut_${index}_axialDepth`] = "More data required";
    } else if (!axialDepth || axialDepth <= 0) {
      errors[`cut_${index}_axialDepth`] = `Cut ${index + 1} axial depth must be greater than zero.`;
    }

    if (cut.cutLength === "" || cut.cutLength === null || cut.cutLength === undefined) {
      errors[`cut_${index}_cutLength`] = "More data required";
    } else if (!cutLength || cutLength <= 0) {
      errors[`cut_${index}_cutLength`] = `Cut ${index + 1} cut length must be greater than zero.`;
    }

    if (cut.passCount === "" || cut.passCount === null || cut.passCount === undefined) {
      errors[`cut_${index}_passCount`] = "More data required";
    } else if (!passCount || passCount <= 0) {
      errors[`cut_${index}_passCount`] = `Cut ${index + 1} pass count must be greater than zero.`;
    }

    return {
      ...cut,
      radialDepth,
      axialDepth,
      cutLength,
      passCount,
    };
  });

  if (Object.keys(errors).length > 0) {
    return { errors, confidence: "Invalid" };
  }

  const toolDiameterMM = convertLengthToMillimeters(toolDiameter, units);
  const stickoutMM = convertLengthToMillimeters(stickoutLength, units);
  const feedRateMMPerMin = convertFeedToMMPerMin(feedRate, units);
  const lengthToDiameterRatio = stickoutMM / toolDiameterMM;
  const surfaceSpeedMPerMin = (Math.PI * toolDiameterMM * spindleRPM) / 1000;

  const perCutResults = [];
  let cumulativeWearPercent = 0;
  let totalCutTimeMinutes = 0;
  let totalRemovedVolumeMM3 = 0;
  let peakWearPercent = 0;

  normalizedCuts.forEach((cut, index) => {
    const radialDepthMM = convertLengthToMillimeters(cut.radialDepth, units);
    const axialDepthMM = convertLengthToMillimeters(cut.axialDepth, units);
    const cutLengthMM = convertLengthToMillimeters(cut.cutLength, units);

    const radialEngagementRatio = Math.min(
      radialDepthMM / Math.max(toolDiameterMM, 0.000001),
      1
    );

    const lengthRatioFactor = 1 + Math.max(0, (lengthToDiameterRatio - 3) * 0.08);
    const axialFactor = 0.65 + axialDepthMM / Math.max(toolDiameterMM, 0.000001);
    const radialFactor = 0.75 + radialEngagementRatio * 1.35;
    const speedFactor = 0.55 + surfaceSpeedMPerMin / 180;
    const feedFactor = 0.65 + feedRateMMPerMin / 450;
    const passFactor = Math.max(1, cut.passCount);
    const difficultyFactor = workpieceMaterial.difficulty;
    const coolantFactor = coolant.multiplier;
    const toolResistanceFactor =
      toolMaterial.wearResistance * toolCoating.wearResistanceMultiplier;

    const cutTimeMinutes =
      ((cutLengthMM * passFactor) / Math.max(feedRateMMPerMin, 0.000001));

    const removedVolumeMM3 =
      radialDepthMM * axialDepthMM * cutLengthMM * passFactor;

    const wearRatePerMinute =
      (
        difficultyFactor *
        radialFactor *
        axialFactor *
        speedFactor *
        feedFactor *
        lengthRatioFactor *
        coolantFactor
      ) /
      (toolResistanceFactor * toolMaterial.baseLifeMinutes);

    const wearPercentForCut = wearRatePerMinute * cutTimeMinutes * 100;
    cumulativeWearPercent += wearPercentForCut;
    totalCutTimeMinutes += cutTimeMinutes;
    totalRemovedVolumeMM3 += removedVolumeMM3;
    peakWearPercent = Math.max(peakWearPercent, cumulativeWearPercent);

    perCutResults.push({
      index: index + 1,
      radialDepth: cut.radialDepth,
      axialDepth: cut.axialDepth,
      cutLength: cut.cutLength,
      passCount: cut.passCount,
      radialDepthMM: roundTo(radialDepthMM, 4),
      axialDepthMM: roundTo(axialDepthMM, 4),
      cutLengthMM: roundTo(cutLengthMM, 4),
      removedVolumeMM3: roundTo(removedVolumeMM3, 3),
      cutTimeMinutes: roundTo(cutTimeMinutes, 4),
      wearPercentForCut: roundTo(wearPercentForCut, 3),
      cumulativeWearPercent: roundTo(cumulativeWearPercent, 3),
      wearStatus: getWearStatus(cumulativeWearPercent),
    });
  });

  if (lengthToDiameterRatio > 5) {
    warnings.push("High stickout increases vibration risk and usually accelerates edge wear.");
  }

  if (surfaceSpeedMPerMin > 250 && params.workpieceMaterial !== "aluminum_6061" && params.workpieceMaterial !== "aluminum_7075" && params.workpieceMaterial !== "brass") {
    warnings.push("Surface speed is high for this material. Wear may increase quickly.");
  }

  if (perCutResults.some((cut) => cut.cumulativeWearPercent >= 80)) {
    warnings.push("Estimated cumulative wear is approaching end-of-life territory.");
  }

  if (perCutResults.some((cut) => cut.wearPercentForCut >= 25)) {
    warnings.push("One or more cuts are especially aggressive and consume tool life quickly.");
  }

  const remainingLifePercent = Math.max(0, 100 - cumulativeWearPercent);
  const projectedToolLifeMinutes =
    totalCutTimeMinutes > 0 && cumulativeWearPercent > 0
      ? (100 / cumulativeWearPercent) * totalCutTimeMinutes
      : 0;

  return {
    perCutResults,
    totalCutTimeMinutes: roundTo(totalCutTimeMinutes, 4),
    totalRemovedVolumeMM3: roundTo(totalRemovedVolumeMM3, 3),
    cumulativeWearPercent: roundTo(cumulativeWearPercent, 3),
    remainingLifePercent: roundTo(remainingLifePercent, 3),
    projectedToolLifeMinutes: roundTo(projectedToolLifeMinutes, 3),
    peakWearPercent: roundTo(peakWearPercent, 3),
    lengthToDiameterRatio: roundTo(lengthToDiameterRatio, 3),
    surfaceSpeedMPerMin: roundTo(surfaceSpeedMPerMin, 3),
    warnings,
    confidence: warnings.length === 0 ? "High" : "Medium",
  };
};

const ToolWearCalculator = ({ onSave, savingCalculation }) => {
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [cuts, setCuts] = useState(DEFAULT_CUTS);
  const [results, setResults] = useState(null);
  const [savedTools, setSavedTools] = useState(() => loadSavedTools());
  const [selectedToolId, setSelectedToolId] = useState("");
  const [toolName, setToolName] = useState("");

  useEffect(() => {
    setResults(calculateToolWear({ params, cuts }));
  }, [params, cuts]);

  const hasErrors = results?.errors && Object.keys(results.errors).length > 0;
  const selectedTool = savedTools.find((tool) => tool.id === selectedToolId);

  const unitLabels = useMemo(() => {
    return params.units === "metric"
      ? {
          diameter: "Tool Diameter (mm)",
          stickout: "Tool Stickout (mm)",
          feedRate: "Feed Rate (mm/min)",
          radialDepth: "X/Y Depth / Radial Engagement (mm)",
          axialDepth: "Z Depth / Axial Depth (mm)",
          cutLength: "Length of Cut (mm)",
        }
      : {
          diameter: 'Tool Diameter (in)',
          stickout: 'Tool Stickout (in)',
          feedRate: 'Feed Rate (in/min)',
          radialDepth: 'X/Y Depth / Radial Engagement (in)',
          axialDepth: 'Z Depth / Axial Depth (in)',
          cutLength: 'Length of Cut (in)',
        };
  }, [params.units]);

  const handleParamChange = (field, value) => {
    setParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleParamNumberChange = (field, value) => {
    setParams((prev) => ({
      ...prev,
      [field]: value === "" ? "" : parseFloat(value),
    }));
  };

  const handleCutChange = (cutId, field, value) => {
    setCuts((prev) =>
      prev.map((cut) =>
        cut.id === cutId
          ? {
          ...cut,
          [field]:
              value === ""
                ? ""
                : field === "passCount"
                  ? parseInt(value, 10)
                  : parseFloat(value),
        }
      : cut
    )
  );
  };

  const addCut = () => {
    setCuts((prev) => [
      ...prev,
      createCut(createCutId(), 0.05, 0.25, 1.0, 1),
    ]);
  };

  const removeCut = (cutId) => {
    setCuts((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((cut) => cut.id !== cutId);
    });
  };

  const resetForm = () => {
    setParams(DEFAULT_PARAMS);
    setCuts(DEFAULT_CUTS);
    setSelectedToolId("");
    setToolName("");
  };

  const saveToolProfile = () => {
    const name = toolName.trim() || `${TOOL_MATERIAL_OPTIONS[params.toolMaterial]?.label || "Tool"} ${params.toolDiameter}`;
    const existingWear = selectedTool?.wearPercent || 0;
    const nextTool = {
      id: selectedToolId || `tool_${Date.now()}`,
      name,
      units: params.units,
      toolMaterial: params.toolMaterial,
      toolCoating: params.toolCoating,
      toolDiameter: params.toolDiameter,
      stickoutLength: params.stickoutLength,
      wearPercent: existingWear,
      updatedAt: new Date().toISOString(),
    };
    const nextTools = selectedToolId
      ? savedTools.map((tool) => (tool.id === selectedToolId ? nextTool : tool))
      : [nextTool, ...savedTools];

    setSavedTools(nextTools);
    writeSavedTools(nextTools);
    setSelectedToolId(nextTool.id);
    setToolName(nextTool.name);
  };

  const loadToolProfile = (toolId) => {
    setSelectedToolId(toolId);
    const tool = savedTools.find((item) => item.id === toolId);
    if (!tool) return;

    setToolName(tool.name);
    setParams((prev) => ({
      ...prev,
      units: tool.units || prev.units,
      toolMaterial: tool.toolMaterial || prev.toolMaterial,
      toolCoating: tool.toolCoating || prev.toolCoating,
      toolDiameter: tool.toolDiameter || prev.toolDiameter,
      stickoutLength: tool.stickoutLength || prev.stickoutLength,
    }));
  };

  const applyWearToSelectedTool = () => {
    if (!selectedToolId || !results || hasErrors) return;
    const nextTools = savedTools.map((tool) => {
      if (tool.id !== selectedToolId) return tool;
      return {
        ...tool,
        wearPercent: Math.min(100, roundTo((tool.wearPercent || 0) + results.cumulativeWearPercent, 2)),
        lastOperation: `${getMaterialLabel(params.workpieceMaterial)} | ${results.totalCutTimeMinutes} min`,
        updatedAt: new Date().toISOString(),
      };
    });
    setSavedTools(nextTools);
    writeSavedTools(nextTools);
  };

  const copyToClipboard = () => {
    if (!results || hasErrors) return;

    const text = [
      "ManuCalc - Tool Wear Calculator",
      `Units: ${params.units === "metric" ? "Metric" : "Imperial"}`,
      `Tool Material: ${TOOL_MATERIAL_OPTIONS[params.toolMaterial]?.label || params.toolMaterial}`,
      `Tool Coating: ${TOOL_COATINGS[params.toolCoating]?.label || params.toolCoating}`,
      `Workpiece Material: ${getMaterialLabel(params.workpieceMaterial)}`,
      `Coolant: ${COOLANT_OPTIONS[params.coolant]?.label || params.coolant}`,
      `Tool Diameter: ${params.toolDiameter}`,
      `Tool Stickout: ${params.stickoutLength}`,
      `Spindle RPM: ${params.spindleRPM}`,
      `Feed Rate: ${params.feedRate}`,
      `Cumulative Wear: ${results.cumulativeWearPercent}%`,
      `Remaining Life: ${results.remainingLifePercent}%`,
      `Projected Tool Life: ${results.projectedToolLifeMinutes} min`,
      `Total Cut Time: ${results.totalCutTimeMinutes} min`,
      `Surface Speed: ${results.surfaceSpeedMPerMin} m/min`,
      ...results.perCutResults.map(
        (cut) =>
          `Cut ${cut.index}: ae=${cut.radialDepth}, ap=${cut.axialDepth}, L=${cut.cutLength}, passes=${cut.passCount}, cut wear=${cut.wearPercentForCut}%, cumulative=${cut.cumulativeWearPercent}%`
      ),
    ].join("\n");

    navigator.clipboard.writeText(text);
  };

  const handleSave = () => {
    if (!results || hasErrors || !onSave) return;
    applyWearToSelectedTool();

    onSave({
      summary: `${getMaterialLabel(params.workpieceMaterial)} | Wear ${results.cumulativeWearPercent}%`,
      input: {
        units: params.units,
        toolMaterial:
          TOOL_MATERIAL_OPTIONS[params.toolMaterial]?.label || params.toolMaterial,
        toolCoating:
          TOOL_COATINGS[params.toolCoating]?.label || params.toolCoating,
        workpieceMaterial: getMaterialLabel(params.workpieceMaterial),
        coolant: COOLANT_OPTIONS[params.coolant]?.label || params.coolant,
        toolDiameter: params.toolDiameter,
        stickoutLength: params.stickoutLength,
        spindleRPM: params.spindleRPM,
        feedRate: params.feedRate,
        cuts: cuts.map((cut, index) => ({
          position: index + 1,
          radialDepth: cut.radialDepth,
          axialDepth: cut.axialDepth,
          cutLength: cut.cutLength,
          passCount: cut.passCount,
        })),
      },
      results: {
        cumulativeWearPercent: results.cumulativeWearPercent,
        remainingLifePercent: results.remainingLifePercent,
        projectedToolLifeMinutes: results.projectedToolLifeMinutes,
        totalCutTimeMinutes: results.totalCutTimeMinutes,
        perCutResults: results.perCutResults,
      },
      warnings: Array.isArray(results.warnings) ? results.warnings : [],
      confidence: results.confidence || "N/A",
    });
  };

  return (
  <div className="calculator-page">
    <div className="calculator-shell">
    
      <div className="calculator-header" style={{ marginBottom: "24px" }}>
        <h1 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
          ⏳ Tool Wear Calculator
        </h1>
        <p style={{ color: "var(--text-muted)", margin: 0 }}>
          Estimate cumulative tool wear across a sequence of cuts using tool
          material, workpiece material, engagement, speed, feed, stickout, and
          pass count.
        </p>
      </div>

      <div className="calculator-panel" style={{ marginBottom: "20px" }}>
        <h3 style={{ marginTop: 0 }}>Saved Tool Tracking</h3>
        <div className="calculator-input-grid three-column">
          <div className="form-group">
            <label>Saved Tool</label>
            <select
              value={selectedToolId}
              onChange={(event) => loadToolProfile(event.target.value)}
            >
              <option value="">New tool profile</option>
              {savedTools.map((tool) => (
                <option key={tool.id} value={tool.id}>
                  {tool.name} - {roundTo(tool.wearPercent || 0, 2)}% wear
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Tool Name / Asset ID</label>
            <input
              value={toolName}
              onChange={(event) => setToolName(event.target.value)}
              placeholder="1/2 carbide end mill - Tool 12"
            />
          </div>
          <div className="form-group">
            <label>Current Tool Wear</label>
            <input
              value={`${roundTo(selectedTool?.wearPercent || 0, 2)}%`}
              readOnly
            />
          </div>
        </div>
        <div className="form-actions" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button type="button" className="btn-secondary" onClick={saveToolProfile}>
            Save Tool Profile
          </button>
          <span style={{ color: "var(--text-muted)", alignSelf: "center" }}>
            Saving a wear calculation adds this operation's estimated wear to the selected tool.
          </span>
        </div>
      </div>

      <div className="calculator-layout calculator-layout-wide-results">
      
        <div className="calculator-panel">
        
          <div
            style={{
              marginBottom: "18px",
              padding: "14px",
              borderRadius: "12px",
              background: "rgba(245, 158, 11, 0.08)",
              border: "1px solid rgba(245, 158, 11, 0.18)",
            }}
          >
            <div
              style={{
                color: "var(--text-primary)",
                fontWeight: 700,
                marginBottom: "6px",
              }}
            >
              Wear Model
            </div>
            <div style={{ color: "var(--text-muted)", fontSize: "14px", lineHeight: 1.6 }}>
              This estimates relative wear progression from setup severity and
              cut sequence. More aggressive cuts, harder materials, higher speed,
              and longer stickout consume tool life faster.
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              htmlFor="units"
              style={{
                display: "block",
                marginBottom: "8px",
                color: "var(--text-primary)",
                fontWeight: 600,
              }}
            >
              Units
            </label>
            <select
              id="units"
              value={params.units}
              onChange={(e) => handleParamChange("units", e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--border-color)",
                background: "var(--bg-surface)",
                color: "var(--text-primary)",
              }}
            >
              <option value="inch">Imperial</option>
              <option value="metric">Metric</option>
            </select>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            <div className="form-group">
              <label
                htmlFor="toolMaterial"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                Tool Material
              </label>
              <select
                id="toolMaterial"
                value={params.toolMaterial}
                onChange={(e) => handleParamChange("toolMaterial", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                }}
              >
                {Object.entries(TOOL_MATERIAL_OPTIONS).map(([key, material]) => (
                  <option key={key} value={key}>
                    {material.label}
                  </option>
                ))}
              </select>
            </div>

            <ToolCoatingSelector
              value={params.toolCoating}
              onChange={(value) => handleParamChange("toolCoating", value)}
              error={results?.errors?.toolCoating}
            />

            <MaterialSelector
              value={params.workpieceMaterial}
              onChange={(value) => handleParamChange("workpieceMaterial", value)}
              error={results?.errors?.workpieceMaterial}
              label="Workpiece Material"
            />

            <div className="form-group">
              <label
                htmlFor="coolant"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                Coolant
              </label>
              <select
                id="coolant"
                value={params.coolant}
                onChange={(e) => handleParamChange("coolant", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                }}
              >
                {Object.entries(COOLANT_OPTIONS).map(([key, item]) => (
                  <option key={key} value={key}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            <div className="form-group">
              <label
                htmlFor="toolDiameter"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                {unitLabels.diameter}
              </label>
              <input
                id="toolDiameter"
                type="number"
                step="0.001"
                min="0"
                value={params.toolDiameter}
                onChange={(e) =>
                  handleParamNumberChange("toolDiameter", e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="stickoutLength"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                {unitLabels.stickout}
              </label>
              <input
                id="stickoutLength"
                type="number"
                step="0.001"
                min="0"
                value={params.stickoutLength}
                onChange={(e) =>
                  handleParamNumberChange("stickoutLength", e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="spindleRPM"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                Spindle RPM
              </label>
              <input
                id="spindleRPM"
                type="number"
                step="1"
                min="0"
                value={params.spindleRPM}
                onChange={(e) =>
                  handleParamNumberChange("spindleRPM", e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="feedRate"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                {unitLabels.feedRate}
              </label>
              <input
                id="feedRate"
                type="number"
                step="0.1"
                min="0"
                value={params.feedRate}
                onChange={(e) =>
                  handleParamNumberChange("feedRate", e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <div
            style={{
              marginBottom: "14px",
              marginTop: "24px",
              color: "var(--text-primary)",
              fontWeight: 700,
              fontSize: "15px",
            }}
          >
            Cut Sequence
          </div>

          <div style={{ display: "grid", gap: "14px", marginBottom: "18px" }}>
            {cuts.map((cut, index) => (
              <div
                key={cut.id}
                style={{
                  padding: "14px",
                  borderRadius: "14px",
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border-color)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    alignItems: "center",
                    marginBottom: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                    }}
                  >
                    Cut {index + 1}
                  </div>

                  {cuts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCut(cut.id)}
                      className="btn-secondary"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label
                      htmlFor={`radialDepth-${cut.id}`}
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-primary)",
                        fontWeight: 600,
                      }}
                    >
                      {unitLabels.radialDepth}
                    </label>
                    <input
                      id={`radialDepth-${cut.id}`}
                      type="number"
                      step="0.001"
                      min="0"
                      value={cut.radialDepth}
                      onChange={(e) =>
                        handleCutChange(cut.id, "radialDepth", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-color)",
                        background: "var(--bg-surface-alt)",
                        color: "var(--text-primary)",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`axialDepth-${cut.id}`}
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-primary)",
                        fontWeight: 600,
                      }}
                    >
                      {unitLabels.axialDepth}
                    </label>
                    <input
                      id={`axialDepth-${cut.id}`}
                      type="number"
                      step="0.001"
                      min="0"
                      value={cut.axialDepth}
                      onChange={(e) =>
                        handleCutChange(cut.id, "axialDepth", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-color)",
                        background: "var(--bg-surface-alt)",
                        color: "var(--text-primary)",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`cutLength-${cut.id}`}
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-primary)",
                        fontWeight: 600,
                      }}
                    >
                      {unitLabels.cutLength}
                    </label>
                    <input
                      id={`cutLength-${cut.id}`}
                      type="number"
                      step="0.001"
                      min="0"
                      value={cut.cutLength}
                      onChange={(e) =>
                        handleCutChange(cut.id, "cutLength", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-color)",
                        background: "var(--bg-surface-alt)",
                        color: "var(--text-primary)",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`passCount-${cut.id}`}
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-primary)",
                        fontWeight: 600,
                      }}
                    >
                      Pass Count
                    </label>
                    <input
                      id={`passCount-${cut.id}`}
                      type="number"
                      step="1"
                      min="1"
                      value={cut.passCount}
                      onChange={(e) =>
                        handleCutChange(cut.id, "passCount", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-color)",
                        background: "var(--bg-surface-alt)",
                        color: "var(--text-primary)",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="form-actions"
            style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
          >
            <button onClick={addCut} className="btn-primary" type="button">
              + Add Cut
            </button>
            <button onClick={resetForm} className="btn-secondary" type="button">
              Reset
            </button>
          </div>
        </div>

       <div className="calculator-panel">
        
          {results && !hasErrors ? (
            <>
              <div
                className="results-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: "20px",
                }}
              >
                <h3 style={{ color: "var(--text-primary)", margin: 0 }}>
                  Results
                </h3>
                <div
                  className="confidence-indicator"
                  style={{ color: "var(--text-secondary)" }}
                >
                  Confidence:{" "}
                  <span
                    style={{ color: "var(--accent)", fontWeight: 700 }}
                  >
                    {results.confidence}
                  </span>
                </div>
              </div>

              <div
                className="results-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "12px",
                  marginBottom: "20px",
                }}
              >
                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Cumulative Wear
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {results.cumulativeWearPercent}%
                  </span>
                </div>

                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Remaining Life
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {results.remainingLifePercent}%
                  </span>
                </div>

                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Projected Tool Life
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {results.projectedToolLifeMinutes} min
                  </span>
                </div>

                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Surface Speed
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {results.surfaceSpeedMPerMin} m/min
                  </span>
                </div>
              </div>

              <div
                style={{
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "10px",
                  padding: "16px",
                  marginBottom: "20px",
                }}
              >
                <h4
                  style={{
                    color: "var(--text-primary)",
                    marginTop: 0,
                    marginBottom: "10px",
                  }}
                >
                  Setup Summary
                </h4>
                <div style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Tool Material:
                    </strong>{" "}
                    {TOOL_MATERIAL_OPTIONS[params.toolMaterial]?.label}
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Workpiece Material:
                    </strong>{" "}
                    {getMaterialLabel(params.workpieceMaterial)}
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Coolant:
                    </strong>{" "}
                    {COOLANT_OPTIONS[params.coolant]?.label}
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      L/D Ratio:
                    </strong>{" "}
                    {results.lengthToDiameterRatio}
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Total Cut Time:
                    </strong>{" "}
                    {results.totalCutTimeMinutes} min
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Total Removed Volume:
                    </strong>{" "}
                    {results.totalRemovedVolumeMM3} mm³
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "10px",
                  padding: "16px",
                  marginBottom: "20px",
                }}
              >
                <h4
                  style={{
                    color: "var(--text-primary)",
                    marginTop: 0,
                    marginBottom: "10px",
                  }}
                >
                  Per-Cut Wear
                </h4>
                <div style={{ display: "grid", gap: "10px" }}>
                  {results.perCutResults.map((cut) => (
                    <div
                      key={cut.index}
                      style={{
                        padding: "12px",
                        borderRadius: "10px",
                        border: "1px solid var(--border-color)",
                        background: "var(--bg-surface-alt)",
                      }}
                    >
                      <div
                        style={{
                          color: "var(--text-primary)",
                          fontWeight: 700,
                          marginBottom: "6px",
                        }}
                      >
                        Cut {cut.index}
                      </div>
                      <div style={{ lineHeight: 1.65 }}>
                        <div>
                          ae: {cut.radialDepth} | ap: {cut.axialDepth} | L: {cut.cutLength} | passes: {cut.passCount}
                        </div>
                        <div>
                          Cut time:{" "}
                          <strong style={{ color: "var(--text-primary)" }}>
                            {cut.cutTimeMinutes} min
                          </strong>
                        </div>
                        <div>
                          Cut wear:{" "}
                          <strong style={{ color: "var(--text-primary)" }}>
                            {cut.wearPercentForCut}%
                          </strong>
                        </div>
                        <div>
                          Cumulative wear:{" "}
                          <strong style={{ color: "var(--text-primary)" }}>
                            {cut.cumulativeWearPercent}%
                          </strong>
                        </div>
                        <div>
                          Status:{" "}
                          <strong style={{ color: "var(--text-primary)" }}>
                            {cut.wearStatus}
                          </strong>
                        </div>
                        <div>Removed volume: {cut.removedVolumeMM3} mm³</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {Array.isArray(results.warnings) && results.warnings.length > 0 && (
                <div
                  className="warnings"
                  style={{
                    background: "rgba(245, 158, 11, 0.12)",
                    border: "1px solid rgba(245, 158, 11, 0.35)",
                    borderRadius: "10px",
                    padding: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <h4
                    style={{
                      color: "var(--text-primary)",
                      marginBottom: "10px",
                    }}
                  >
                    ⚠️ Warnings
                  </h4>
                  <ul
                    style={{
                      paddingLeft: "20px",
                      color: "var(--text-secondary)",
                      marginBottom: 0,
                    }}
                  >
                    {results.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div
                className="results-actions"
                style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
              >
                <button
                  onClick={handleSave}
                  className="btn-primary"
                  type="button"
                  disabled={savingCalculation}
                >
                  {savingCalculation ? "Saving..." : "💾 Save Calculation"}
                </button>
                <button
                  onClick={copyToClipboard}
                  className="btn-secondary"
                  type="button"
                >
                  📄 Copy
                </button>
              </div>
            </>
          ) : (
            <div
              style={{
                color: "var(--text-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "180px",
                textAlign: "center",
              }}
            >
              Enter valid inputs to estimate tool wear across multiple cuts.
            </div>
          )}
               </div>
      </div>
    </div>
  </div>
  );
};

export default ToolWearCalculator;
